import {Component, inject, OnInit} from '@angular/core';
import {ProductService} from './product.service';
import {SpinnerOverlayService} from '../../../Components/spinner/spinner-overlay.service';
import {NgClass} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {ProductUpdateComponent} from '../product-update/product-update.component';
import {MessageService} from '../../../shared/message.service';
import {ITEMS_PER_PAGE} from '../../../core/config/pagination.constants';
import {PaginatorState} from 'primeng/paginator';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {ProductDeleteComponent} from '../product-delete/product-delete.component';
import {IProduct} from '../../../shared/model/product.model';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CustomInput} from '../../../Components/input/custom-input';
import {Button} from '../../../Components/button/button';
import * as XLSX from 'xlsx';
import {ProductMassiveLoadResponse} from './product.service';
import { MassiveLoadError } from '../../../shared/model/massive-load.model';
import { MassiveLoadErrorsComponent } from '../../../shared/massive-load-errors/massive-load-errors.component';
import { MassiveLoadTemplateDialog, TemplateDownloadScope } from '../../../shared/massive-load-template-dialog/massive-load-template-dialog';

@Component({
  selector: 'app-product',
  imports: [
    NgClass,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    CustomInput,
    Button,
    MassiveLoadErrorsComponent
  ],
  templateUrl: './product.component.html',
  styleUrl: './product.component.css'
})
export class Product implements OnInit {
  productList: IProduct[] = []

  messageService = inject(MessageService);

  totalItems: number = 2
  first = 0;
  rows = ITEMS_PER_PAGE;
  page = 0;
  sortField = 'id';
  sortOrder = 0;
  lastEvent: any;
  product: string | null = null
  massiveLoadErrors: MassiveLoadError[] = [];
  totalMassiveLoadErrors = 0;
  showMassiveLoadErrors = false;

  constructor(private productService: ProductService, private spinner: SpinnerOverlayService, private dialog: MatDialog) {
  }

  ngOnInit() {
    this.findAll()
  }

  findAll(reset: boolean = false) {
    this.handleLoading(true)

    if (reset) {
      this.sortField = "id"
      this.sortOrder = 0
    }
    const params = {
      page: reset ? 0 : this.page,
      size: this.rows,
      sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`,
      criteria: this.product
    };

    this.productService.findByParams(params).subscribe({
      next: (response) => {
        this.productList = response.body?.product.productList ?? []
        this.totalItems = Number(response.body?.product.totalCount);
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  getBackgroundClasses(active: boolean) {
    if (active) return 'bg-green-100';
    return 'bg-red-100';
  }

  openEditModal(id: number | null) {
    const dialogRef = this.dialog.open(ProductUpdateComponent, {
      width: '90%',
      height: 'auto',
      data: id ? {
        id: id
      } : null,
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.findAll(true)
        this.messageService.showSuccess("Producto actualizado con exito")
      }
    });
  }

  openActivateProductModal(name: string, id: number, active: boolean = false) {
    const dialogRef = this.dialog.open(ProductDeleteComponent, {

      data: {
        name: name,
        active: active
      },
      panelClass: 'lexend-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.activeOrInactiveUser(id, active)
      }
    });
  }

  activeOrInactiveUser(id: number, active: boolean) {
    this.handleLoading(true)
    this.productService.activate(active, id).subscribe({
      next: () => {
        this.findAll()
        const message = active ? "activado" : "desactivado"
        this.messageService.showSuccess(`Producto ${message} con exito`)
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor((this.first ?? 0) / (this.rows ?? 10));
    this.findAll(true);
  }

  loadProducts(event: TableLazyLoadEvent) {
    if (JSON.stringify(event) === JSON.stringify(this.lastEvent)) {
      return;
    }
    this.lastEvent = event;

    this.sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField ?? 'startDate';
    this.sortOrder = event.sortOrder ?? 1;
    this.page = Math.floor((event.first ?? 0) / (event.rows ?? 10));
    this.rows = event.rows ?? 10;

    this.findAll();
  }

  cleanFilter() {
    this.product = null;
    this.findAll(true);
  }

  downloadTemplate() {
    if (!this.productList.length) {
      this.messageService.showError('No hay productos consultados para generar la plantilla.');
      return;
    }

    const dialogRef = this.dialog.open(MassiveLoadTemplateDialog, {
      width: '32rem',
      panelClass: 'lexend-dialog',
      disableClose: true,
      data: {
        entityLabel: 'productos',
        filteredCount: this.hasProductFilter() ? this.totalItems : this.productList.length
      }
    });

    dialogRef.afterClosed().subscribe((scope: TemplateDownloadScope | undefined) => {
      if (scope === 'FILTERED') {
        if (this.hasProductFilter()) {
          this.downloadProductsForTemplate(this.product, this.totalItems);
        } else {
          this.createTemplate(this.productList);
        }
      } else if (scope === 'ALL') {
        this.downloadAllProductsForTemplate();
      }
    });
  }

  private hasProductFilter(): boolean {
    return (this.product?.trim().length ?? 0) > 0;
  }

  private downloadAllProductsForTemplate() {
    this.handleLoading(true);
    this.productService.findByParams(this.templateParams(null, 1)).subscribe({
      next: response => {
        const total = Number(response.body?.product.totalCount ?? 0);
        this.downloadProductsForTemplate(null, total);
      },
      error: () => {
        this.handleLoading(false);
        this.messageService.showError('No se pudieron consultar todos los productos.');
      }
    });
  }

  private downloadProductsForTemplate(criteria: string | null, total: number) {
    if (total <= 0) {
      this.handleLoading(false);
      this.messageService.showError('No hay productos disponibles para generar la plantilla.');
      return;
    }

    this.handleLoading(true);
    this.productService.findByParams(this.templateParams(criteria, total)).subscribe({
      next: response => {
        this.handleLoading(false);
        const products = response.body?.product.productList ?? [];
        this.createTemplate(products);
      },
      error: () => {
        this.handleLoading(false);
        this.messageService.showError('No se pudieron consultar los productos para generar la plantilla.');
      }
    });
  }

  private templateParams(criteria: string | null, size: number) {
    return {
      page: 0,
      size,
      sort: `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`,
      criteria
    };
  }

  private createTemplate(products: IProduct[]) {
    if (!products.length) {
      this.messageService.showError('No hay productos consultados para generar la plantilla.');
      return;
    }

    const headers = [
      'CATEGORIA',
      'TIPO PRODUCTO',
      'ACABADO',
      'NOMBRE',
      'DESCRIPCION',
      'USO Y APLICACIONES',
      'DISEÑO',
      'PALABRAS CLAVE',
      'ACTIVO'
    ];

    const rows = products.map(product => ({
      'CATEGORIA': this.normalizeExcelText(product.category),
      'TIPO PRODUCTO': this.normalizeExcelText(product.productType),
      'ACABADO': this.normalizeExcelText(product.finish),
      'NOMBRE': this.normalizeExcelText(product.name),
      'DESCRIPCION': this.normalizeExcelText(product.description),
      'USO Y APLICACIONES': this.normalizeExcelText(product.usesAndApplications),
      'DISEÑO': this.normalizeExcelText(product.design),
      'PALABRAS CLAVE': this.normalizeExcelText(product.keywords),
      'ACTIVO': product.active ? 'T' : 'F'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, {header: headers});
    worksheet['!cols'] = [
      {wch: 20}, {wch: 22}, {wch: 22}, {wch: 28}, {wch: 36},
      {wch: 36}, {wch: 36}, {wch: 36}, {wch: 12}
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Productos');
    XLSX.writeFile(workbook, 'plantilla-productos.xlsx');
  }

  private normalizeExcelText(value: string | null | undefined): string {
    return (value ?? '').replace(/\r\n?/g, '\n');
  }

  onProductFileSelected(event: Event, fileInput: HTMLInputElement) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.messageService.showError('Seleccione un archivo Excel con extensión .xlsx.');
      return;
    }

    this.massiveLoadErrors = [];
    this.totalMassiveLoadErrors = 0;
    this.showMassiveLoadErrors = false;
    this.handleLoading(true);
    this.productService.massiveLoad(file).subscribe({
      next: response => {
        this.handleLoading(false);
        this.handleMassiveLoadResponse(response.body);
        this.findAll();
      },
      error: error => {
        this.handleLoading(false);
        const response = error?.error as ProductMassiveLoadResponse | undefined;
        const technicalError = response?.technicalError;
        this.messageService.showError(
          technicalError || 'No se pudo procesar la carga masiva de productos.'
        );
      }
    });
  }

  private handleMassiveLoadResponse(response: ProductMassiveLoadResponse | null) {
    if (!response) {
      this.messageService.showError('El servidor no devolvió un resultado válido.');
      return;
    }

    if (response.technicalError) {
      this.messageService.showError(response.technicalError);
      return;
    }

    const errors = [...(response.errors ?? [])].sort((first, second) =>
      first.rowNumber - second.rowNumber
    );
    this.massiveLoadErrors = errors.map(error => ({
      ...error,
      entityType: 'PRODUCT' as const
    }));
    this.totalMassiveLoadErrors = response.totalErrors ?? errors.length;
    this.showMassiveLoadErrors = false;

    if (!errors.length) {
      this.messageService.showMassiveLoadSuccess(
        `Carga completada. ${response.updatedRows} producto(s) actualizado(s).`
      );
      return;
    }

    if (response.updatedRows > 0) {
      this.messageService.showMassiveLoadSuccess(
        `Carga parcial completada. ${response.updatedRows} producto(s) actualizado(s).`
      );
    }
    this.messageService.showErrorHtml(
      `Se encontraron ${this.totalMassiveLoadErrors} error(es). Presione "Ver errores" para consultar el detalle.`
    );
  }
}
